import type { DragEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, theme } from 'antd';
import {
  CheckCircleOutlined,
  InboxOutlined,
  MessageOutlined,
  MinusCircleOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import type { Lead, LeadStatus } from '@entities/crm';
import { LeadCard } from './lead-card';

const { Text } = Typography;

/**
 * Stage styling mirrors the dashboard KPI cards: the same brand hues, the same
 * `rgba(...,0.18) → transparent` wash and the same white icon tile, so the board
 * reads as part of the app rather than a separate widget.
 */
interface StageStyle {
  accent: string;
  rgb: string;
  icon: ReactNode;
}

const STAGE: Record<LeadStatus, StageStyle> = {
  NEW: { accent: '#2563eb', rgb: '37,99,235', icon: <InboxOutlined /> },
  IN_TOUCH: { accent: '#d97706', rgb: '245,158,11', icon: <MessageOutlined /> },
  QUOTED: { accent: '#8b5cf6', rgb: '139,92,246', icon: <SolutionOutlined /> },
  WON: { accent: '#10b981', rgb: '16,185,129', icon: <CheckCircleOutlined /> },
  LOST: { accent: '#94a3b8', rgb: '148,163,184', icon: <MinusCircleOutlined /> },
};

interface BoardColumnProps {
  status: LeadStatus;
  leads: Lead[];
  hover: boolean;
  draggingId: string | null;
  onDragOver: (status: LeadStatus, e: DragEvent<HTMLDivElement>) => void;
  onDrop: (status: LeadStatus, e: DragEvent<HTMLDivElement>) => void;
  onDragStartCard: (id: string) => void;
  onDragEndCard: () => void;
  onSelect: (id: string) => void;
}

export function BoardColumn({
  status,
  leads,
  hover,
  draggingId,
  onDragOver,
  onDrop,
  onDragStartCard,
  onDragEndCard,
  onSelect,
}: BoardColumnProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { accent, rgb, icon } = STAGE[status];

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver(status, e);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(status, e);
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minHeight: 420,
        padding: 14,
        borderRadius: 16,
        background: hover
          ? `linear-gradient(135deg, rgba(${rgb},0.30), rgba(${rgb},0.06))`
          : `linear-gradient(135deg, rgba(${rgb},0.16), rgba(${rgb},0))`,
        border: `1px solid ${hover ? accent : token.colorBorderSecondary}`,
        boxShadow: hover ? `0 0 0 3px rgba(${rgb},0.12)` : 'none',
        transition: 'background 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          aria-hidden
          style={{
            width: 30,
            height: 30,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 10,
            background: token.colorBgContainer,
            color: accent,
            fontSize: 16,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          }}
        >
          {icon}
        </span>
        <Text style={{ fontWeight: 600, fontSize: 13, flex: 1, minWidth: 0 }} ellipsis>
          {t(`crm.columns.${status}`)}
        </Text>
        <span
          style={{
            minWidth: 22,
            height: 22,
            padding: '0 7px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 11,
            background: token.colorBgContainer,
            color: accent,
            fontSize: 12,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          }}
        >
          {leads.length}
        </span>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {leads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            accent={accent}
            dragging={draggingId === lead.id}
            onDragStart={() => onDragStartCard(lead.id)}
            onDragEnd={onDragEndCard}
            onClick={() => onSelect(lead.id)}
          />
        ))}

        {leads.length === 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '22px 12px',
              borderRadius: 12,
              border: `1px dashed ${hover ? accent : token.colorBorderSecondary}`,
              color: token.colorTextQuaternary,
              fontSize: 12,
              textAlign: 'center',
              transition: 'border-color 200ms ease',
            }}
          >
            {t('crm.columnEmpty')}
          </div>
        ) : null}
      </div>
    </div>
  );
}
